import { useCallback } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, usePropertyField, EditableToken } from '@vibesites/runtime/react';

/**
 * Standalone CTA that opens the CORT lead form modal via window.Cort_modal.open().
 * Renders nothing unless the property has a `custom_cort_lead_form_id` set — that
 * same field is what loads the CORT script, so the button is meaningless without it.
 * Button text is an editable content token (override per page/property).
 */
export function CortLeadFormCta() {
  const cortLeadFormId = (usePropertyField('custom_cort_lead_form_id') as string) || '';
  const buttonText = useContentToken('cortleadformcta.button_text');

  const openCortModal = useCallback(() => {
    const cort =
      typeof window !== 'undefined'
        ? (window as unknown as { Cort_modal?: { open?: () => void } }).Cort_modal
        : undefined;
    cort?.open?.();
  }, []);

  if (!cortLeadFormId) return null;

  return (
    <section className="relative z-10 bg-background pb-0 pt-10 md:pb-0 md:pt-14">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={openCortModal}
          className="cort-cta-btn group/cta relative inline-flex max-w-full items-center justify-center overflow-hidden px-6 py-3 font-body text-sm font-normal normal-case text-secondary-foreground transition-all duration-300 sm:px-10 sm:py-4 sm:text-base"
        >
          <EditableToken
            path="cortleadformcta.button_text"
            as="span"
            className="relative z-10"
          >
            {buttonText || 'Furnish Your Home with CORT'}
          </EditableToken>
        </button>
        <style>{`
          .cort-cta-btn {
            background: var(--vibe-color-secondary);
            border: 1.5px solid var(--vibe-color-secondary);
            border-radius: 9999px;
            box-shadow: none;
          }
          .cort-cta-btn::before {
            content: '';
            position: absolute;
            inset: 0;
            background: #000;
            opacity: 0;
            transition: opacity 0.25s ease;
          }
          .cort-cta-btn:hover {
            transform: translateY(-2px);
            box-shadow:
              0 2px 6px rgba(0,0,0,0.1),
              0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 30%, transparent);
          }
          .cort-cta-btn:hover::before {
            opacity: 0.14;
          }
          .cort-cta-btn:active {
            transform: translateY(0);
            box-shadow: none;
          }
        `}</style>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CortLeadFormCta',
  displayName: 'CORT Lead Form CTA',
  tokens: {
    'cortleadformcta.button_text': {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Furnish Your Home with CORT',
    },
  },
};

(CortLeadFormCta as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default CortLeadFormCta;
